/**
 * Custom Product Card - Navvyad Design
 * Handles pack size selection and add to cart functionality
 */

class CustomProductCard {
  constructor(element) {
    this.element = element;
    this.packOptions = element.querySelectorAll('.m-custom-card__pack-option');
    this.addToCartBtn = element.querySelector('.m-custom-card__add-to-cart');
    this.variantInput = element.querySelector('input[name="id"]');
    
    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Pack option selection
    this.packOptions.forEach((option) => {
      option.addEventListener('click', (e) => this.handlePackSelection(e));
    });

    // Add to cart button uses theme's quick view - no custom handler needed
    // The m-product-quickview-button class triggers theme's event delegation
  }

  handlePackSelection(event) {
    const selectedOption = event.currentTarget;
    const variantId = selectedOption.dataset.variantId;

    if (!variantId || selectedOption.disabled) return;

    // Remove selected class from all options
    this.packOptions.forEach(option => {
      option.classList.remove('m-custom-card__pack-option--selected');
    });

    // Add selected class to clicked option
    selectedOption.classList.add('m-custom-card__pack-option--selected');

    // Update hidden input
    if (this.variantInput) {
      this.variantInput.value = variantId;
    }

    // Update add to cart button data
    if (this.addToCartBtn) {
      this.addToCartBtn.dataset.variantId = variantId;
    }

    // Update price dynamically
    this.updatePrice(variantId);
  }

  async updatePrice(variantId) {
    try {
      const productHandle = this.element.dataset.productHandle;
      
      if (!productHandle) {
        console.warn('Product handle not found');
        return;
      }

      // Add loading state
      const priceWrapper = this.element.querySelector('.m-custom-card__price-wrapper');
      if (priceWrapper) {
        priceWrapper.style.opacity = '0.5';
      }
      
      // Fetch product data
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) return;
      
      const productData = await response.json();
      const variant = productData.variants.find(v => v.id == variantId);
      
      if (!variant) return;

      // Update current price
      const currentPriceEl = this.element.querySelector('.m-custom-card__price--current');
      if (currentPriceEl) {
        currentPriceEl.textContent = this.formatMoney(variant.price);
      }

      // Update compare at price and discount badge
      const comparePriceEl = this.element.querySelector('.m-custom-card__price--compare');
      const discountBadgeEl = this.element.querySelector('.m-custom-card__discount-badge');
      
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        // Show compare price and discount
        if (comparePriceEl) {
          comparePriceEl.textContent = this.formatMoney(variant.compare_at_price);
          comparePriceEl.style.display = '';
        }
        
        // Calculate and show discount
        const discount = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
        if (discountBadgeEl) {
          discountBadgeEl.textContent = `${discount}% OFF`;
          discountBadgeEl.style.display = '';
        }
      } else {
        // Hide compare price and discount if not on sale
        if (comparePriceEl) comparePriceEl.style.display = 'none';
        if (discountBadgeEl) discountBadgeEl.style.display = 'none';
      }

      // Update availability
      if (this.addToCartBtn) {
        if (variant.available) {
          this.addToCartBtn.disabled = false;
          this.addToCartBtn.textContent = 'ADD TO CART';
        } else {
          this.addToCartBtn.disabled = true;
          this.addToCartBtn.textContent = 'SOLD OUT';
        }
      }

      // Remove loading state
      if (priceWrapper) {
        priceWrapper.style.opacity = '1';
      }

    } catch (error) {
      console.error('Error updating price:', error);
      
      // Remove loading state on error
      const priceWrapper = this.element.querySelector('.m-custom-card__price-wrapper');
      if (priceWrapper) {
        priceWrapper.style.opacity = '1';
      }
    }
  }

  getProductHandle() {
    // Get product handle from data attribute
    return this.element.dataset.productHandle || null;
  }

  formatMoney(cents) {
    // Use the theme's global formatMoney function if available
    if (typeof window.formatMoney === 'function' && window.MinimogSettings?.money_format) {
      return window.formatMoney(cents, window.MinimogSettings.money_format);
    }
    
    // Fallback: Basic money formatting
    const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    
    // Try to get currency symbol from existing price element
    const existingPrice = this.element.querySelector('.m-custom-card__price--current');
    if (existingPrice) {
      const text = existingPrice.textContent;
      const currencyMatch = text.match(/[₹$€£¥]/);
      if (currencyMatch) {
        return `${currencyMatch[0]}${amount}`;
      }
    }
    
    // Default to rupee symbol
    return `₹${amount}`;
  }

  handleAddToCart(event) {
    // The button has m-product-quickview-button class
    // Theme's quick view handler will automatically handle this
    // No need for custom logic - just let the event bubble up
  }
}

// Initialize all custom product cards
function initCustomProductCards() {
  const cards = document.querySelectorAll('.m-product-card--custom');
  cards.forEach(card => {
    if (!card.customProductCard) {
      card.customProductCard = new CustomProductCard(card);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustomProductCards);
} else {
  initCustomProductCards();
}

// Re-initialize when new content is loaded (for AJAX sections)
document.addEventListener('shopify:section:load', initCustomProductCards);

// Also try to initialize after a short delay (for dynamic content)
setTimeout(initCustomProductCards, 500);

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CustomProductCard;
}
